import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  getDbConnectionList,
  type DbConnectionInfo,
} from '../../services/data-integration-api';

/**
 * 型录管理入口 — 列出所有源库连接，点击进入型录详情
 */
export default function CatalogGateway() {
  const navigate = useNavigate();
  const [connections, setConnections] = useState<DbConnectionInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const result = await getDbConnectionList({ connection_type: 'source', page, pageSize });
      setConnections(result.items);
      setTotal(result.total);
    } catch (e) {
      console.error('获取源库列表失败:', e);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const dbTypeLabel = (type: string) =>
    type === 'mysql' ? 'MySQL' : type === 'postgresql' ? 'PostgreSQL' : type;

  return (
    <div>
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <h2 className="text-base font-semibold">型录管理</h2>
      </div>

      <div className="p-6">
        {loading ? (
          <p className="text-muted-foreground">加载中...</p>
        ) : fetchError ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">加载失败，请重试</p>
            <Button onClick={fetchList}>重试</Button>
          </div>
        ) : connections.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">暂无源库连接，请先创建源库连接</p>
            <Button onClick={() => navigate('/data-integration/connections')}>
              前往连接管理
            </Button>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>连接名称</TableHead>
                  <TableHead>数据库类型</TableHead>
                  <TableHead>主机</TableHead>
                  <TableHead>型录摘要</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.map(conn => (
                  <TableRow key={conn._id}>
                    <TableCell className="font-medium">{conn.connection_name}</TableCell>
                    <TableCell>{dbTypeLabel(conn.db_type)}</TableCell>
                    <TableCell className="font-mono">{conn.host}:{conn.port}</TableCell>
                    <TableCell>
                      {conn.catalog_table_count && conn.catalog_table_count > 0 ? (
                        <span className="text-sm">
                          <Badge variant="secondary">{conn.catalog_table_count} 张表</Badge>
                          <span className="ml-1 text-muted-foreground">v{conn.catalog_version}</span>
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">未构建</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/data-integration/catalog/${conn._id}`)}
                      >
                        查看型录
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {total > pageSize && (
              <div className="mt-4 flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  上一页
                </Button>
                <span className="text-sm text-muted-foreground">
                  第 {page} 页 / 共 {Math.ceil(total / pageSize)} 页
                </span>
                <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)}>
                  下一页
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
